/**
 * PayerErrors should always have a .code that can be resolved to a translated string, e.g.:
 * * error.server.unavailable
 * * error.bankid.consumed
 *
 * If we don't have a translation for .code, we'll default to displaying the .message instead.
 * If .detail is provided, it will always be displayed.
 *
 * .code, .message, and .detail can all either be provided by the server if the error occurs there,
 * or the frontend when the server was unreachable, or when communication was successful but
 * the response was one which would still be considered an error by an end user, e.g. the bankid
 * token had already been consumed.
 */
export class PayerError extends Error {
  code?: string;
  constructor({
    name = 'PayerError',
    code,
    message = 'Unknown communication error',
    detail,
  }: {
    name?: string;
    code?: string;
    message?: string;
    detail?: string;
  }) {
    super(message);
    this.name = name;
    this.code = code ? `error.${code}` : undefined;
    // only message, name, code, and stack are relayed by createAsyncThunk()
    // (and only as strings at that!) so let's save .detail as .stack for now... :(
    this.stack = detail;
  }
}

export class HttpUnauthorizedError extends PayerError {
  constructor() {
    super({
      name: 'HttpUnauthorizedError',
      code: 'unauthorized',
    });
  }
}

/**
 * Server response status was not 2xx, or the response was not well formatted.
 */
export class ApiResponseError extends PayerError {
  constructor({
    name = 'ApiResponseError',
    code,
    message = '',
    detail,
  }: {
    name?: string;
    code?: string;
    message?: string;
    detail?: string;
  }) {
    super({
      name,
      code: code ? `apiResponse.${code}` : undefined,
      message,
      detail,
    });
  }
}

/**
 * Server responded with Content-Type: application/json, but body was invalid json
 */
export class ServerResponseJsonSyntaxError extends PayerError {
  constructor({ responseText = undefined, cause }: { responseText?: string; cause?: any }) {
    super({
      name: 'ServerResponseJsonSyntaxError',
      code: 'serverResponseJsonSyntax',
      detail: `Server responded with Content-Type: application/json, but body was invalid json: '${responseText}'
cause: ${cause?.message}`,
    });
  }
}
